import { useEffect, useState } from "react";
import { Button, Chip } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api-client";
import { MAINTENANCE_RETURN_KEY } from "./constants";

function getSafeTarget(target?: string | null): string | null {
  if (!target || !target.startsWith("/")) return null;
  if (target === "/maintenance") return null;
  try {
    const url = new URL(target, window.location.origin);
    return url.origin === window.location.origin
      ? url.pathname + url.search + url.hash
      : null;
  } catch {
    return null;
  }
}

export const Maintenance = () => {
  // Read the (validated) return target from sessionStorage once on mount via
  // lazy initial state instead of mirroring it in after the first render.
  const [retryTarget] = useState(() => {
    if (typeof window === "undefined") return "/";

    try {
      const storedTarget = window.sessionStorage.getItem(
        MAINTENANCE_RETURN_KEY,
      );
      return getSafeTarget(storedTarget) ?? "/";
    } catch (storageError) {
      console.warn("Unable to read maintenance return target", storageError);
      return "/";
    }
  });

  const {
    data: versionData,
    isFetching: isVersionFetching,
    refetch: refetchVersion,
  } = api.app.useGetAppVersion();

  useEffect(() => {
    if (versionData?.maintenance === false) {
      const safe = getSafeTarget(retryTarget);

      try {
        window.sessionStorage.removeItem(MAINTENANCE_RETURN_KEY);
      } catch (e) {
        console.warn("Unable to clear maintenance return target", e);
      }

      if (safe && safe !== window.location.pathname) {
        window.location.replace(safe);
      } else {
        window.location.reload();
      }
    }
  }, [versionData?.maintenance, retryTarget]);

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-6 py-12 text-center">
      <Helmet>
        <title>{t`Maintenance`}</title>
      </Helmet>

      <div className="max-w-lg space-y-8">
        <Chip
          variant="flat"
          color="success"
          className="px-4 py-2 text-sm font-medium"
        >
          <span aria-hidden className="mr-2">
            🛠️
          </span>
          <Trans>Načrtovano vzdrževanje v teku</Trans>
        </Chip>

        <header className="space-y-3">
          <h1 className="text-3xl font-semibold text-slate-900 md:text-4xl">
            <Trans>Takoj bomo nazaj</Trans>
          </h1>
          <p className="text-base leading-relaxed text-slate-600 md:text-lg">
            <Trans>
              Izvajamo posodobitve za boljše delovanje aplikacije. Aplikacija bo
              ponovno na voljo kmalu. Hvala za razumevanje.
            </Trans>
          </p>
        </header>

        <div className="flex items-center justify-center gap-4">
          <Button
            color="primary"
            size="lg"
            onPress={() => refetchVersion()}
            className="px-8"
            isLoading={isVersionFetching}
          >
            <Trans>Poskusi ponovno</Trans>
          </Button>
        </div>
      </div>
    </main>
  );
};
